import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHeart, FaRegHeart } from 'react-icons/fa';
import RetroLoadingDots from './RetroLoadingDots';
import { memo } from 'react';

const ImageCardSmall = memo(({
  image,
  isFavorited,
  isNew,
  onToggleFavorite,
  onClick,
  layoutId,
  isSelected,
  onImageLoaded,
  resetRotationKey,
  renderOverlay,
  isGenerating,
  disableBackgroundEffects = false,
  hasBlackBackground = false,
  isAnimated = true
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [localFavorited, setLocalFavorited] = useState(isFavorited);
  const [showFloatingHeart, setShowFloatingHeart] = useState(false);
  const imageRef = useRef(null);
  const [loadError, setLoadError] = useState(false);

  // Random rotations that change on hover
  const [randomRotation] = useState(() => Math.random() * 4 - 2); // -2 to +2 degrees

  // Memoize image URLs
  const { thumbnailUrl, fullSizeUrl } = useMemo(() => {
    // Don't process URLs for placeholder images during generation
    if (image.id.startsWith('placeholder-')) {
      return { thumbnailUrl: null, fullSizeUrl: null };
    }
    
    // Get both URLs upfront
    const fullSize = image.url || image.s3Url;
    const thumbnail = image.thumbnailUrl;
    
    // Validate URLs
    const validFullSize = fullSize?.startsWith('http') ? fullSize : null;
    const validThumbnail = thumbnail?.startsWith('http') ? thumbnail : null;
    
    return {
      thumbnailUrl: validThumbnail,
      fullSizeUrl: validFullSize
    };
  }, [image.thumbnailUrl, image.s3Url, image.url, image.id]);

  const handleImageLoad = () => {
    setIsLoaded(true);
    setLoadError(false);
    onImageLoaded?.(image.id);
  };

  const handleImageError = (e) => {
    setLoadError(true);
    console.error('[RETRO] ❌ ImageCardSmall load error:', {
      imageId: image.id,
      url: e.target.src,
      timestamp: new Date().toISOString()
    });
    
    // If thumbnail fails and we have a different full size URL, try that
    if (fullSizeUrl && (!e.target.src.includes(fullSizeUrl))) {
      if (imageRef.current) {
        imageRef.current.src = '';
        setTimeout(() => {
          if (imageRef.current) {
            imageRef.current.src = fullSizeUrl;
          }
        }, 0);
      }
    }
  };

  // Handle favorite click with local animation
  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    const newFavorited = !localFavorited;
    setLocalFavorited(newFavorited);
    
    // Trigger floating heart animation if favoriting
    if (newFavorited) {
      setShowFloatingHeart(true);
      setTimeout(() => setShowFloatingHeart(false), 1000);
    }
    
    onToggleFavorite?.();
  };

  // Update localFavorited when isFavorited prop changes
  useEffect(() => {
    setLocalFavorited(isFavorited);
  }, [isFavorited]);

  const handleClick = (e) => {
    onClick?.(image);
  };

  return (
    <motion.div
      className="relative group aspect-square hover:z-10"
      layoutId={layoutId}
      style={{
        margin: '2px', // Reduced margin for tighter spacing in compact mode
        overflow: 'visible'
      }}
      whileHover={{ scale: 1.02 }}
      transition={{
        duration: 0.2,
        ease: "easeIn"
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      {/* Main card */}
      <motion.div 
        className="relative w-full h-full cursor-pointer aspect-square"
        style={{
          position: 'relative',
          zIndex: 2
        }}
        onClick={handleClick}
        animate={{
          scale: isHovered ? 1.02 : 1,
          rotate: isHovered ? randomRotation * 0.5 : 0, // Subtle rotation for compact
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 25,
          mass: 1
        }}
      >
        {/* Image container */}
        <div className={`w-full h-full rounded-lg aspect-square ${hasBlackBackground ? 'bg-black' : ''}`}>
          <img
            ref={imageRef}
            src={thumbnailUrl || ''}
            alt={image.prompt}
            crossOrigin="anonymous"
            className={`w-full h-full object-cover rounded-lg transition-opacity duration-300 ease-in-out ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading="lazy"
          />
        </div>

        {/* Overlay - only for placeholder images during generation */}
        {image.id.startsWith('placeholder-') && renderOverlay && (
          <div 
            className="absolute inset-0 rounded-lg overflow-hidden aspect-square"
            style={{ 
              position: 'absolute',
              inset: 0
            }}
          >
            {renderOverlay()}
          </div>
        )}

        {/* Only show favorite heart on hover in compact mode - very minimal */}
        {isHovered && !image.id.startsWith('placeholder-') && isLoaded && (
          <motion.button
            className="absolute top-1 left-1 p-1 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
            style={{ zIndex: 4 }}
            onClick={handleFavoriteClick}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
          >
            {/* Floating heart animation */}
            <AnimatePresence>
              {showFloatingHeart && (
                <motion.div
                  initial={{ opacity: 1, scale: 1, y: 0 }}
                  animate={{ opacity: 0, scale: 1.5, y: -15 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="absolute left-1 pointer-events-none"
                >
                  <FaHeart className="w-3 h-3 text-red-500" />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Static heart - smaller for compact mode */}
            <motion.div
              animate={{ 
                scale: localFavorited ? [1, 1.2, 1] : 1,
                rotate: localFavorited ? [0, -10, 10, 0] : 0
              }}
              transition={{ duration: 0.4 }}
            >
              {localFavorited ? (
                <FaHeart className="w-3 h-3 text-red-500" />
              ) : (
                <FaRegHeart className="w-3 h-3 text-white" />
              )}
            </motion.div>
          </motion.button>
        )}
      </motion.div>
    </motion.div>
  );
});

export default ImageCardSmall; 